import React, { useState, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface MeetingDispositionMultiselectProps {
  selectedValues: string[];
  onChange: (values: string[]) => void;
  isFetching?: boolean;
  className?: string;
}

// All possible meeting disposition values from recommended_deal_qualification
// Now includes qualification type variants (DEAL/ZERO) for TRANSACTIONAL and ENTERPRISE
const ALL_DISPOSITION_OPTIONS = [
  { value: 'TRANSACTIONAL', label: 'Transactional Deal' },
  { value: 'ENTERPRISE', label: 'Enterprise (All)', isParent: true, children: ['ENTERPRISE_DEAL', 'ENTERPRISE_ZERO'] },
  { value: 'ENTERPRISE_DEAL', label: 'Enterprise Deal', indent: true, parent: 'ENTERPRISE' },
  { value: 'ENTERPRISE_ZERO', label: 'Enterprise Stage Zero', indent: true, parent: 'ENTERPRISE' },
  { value: 'NURTURE', label: 'Nurture' },
  { value: 'NOT_ANALYZED', label: 'No Qualification' },
  { value: 'BAD_FIT', label: 'Bad Fit' },
  { value: 'TRASH', label: 'Trash' }
];

// Default "quality leads" filter - excludes BAD_FIT and TRASH
const DEFAULT_QUALITY_FILTER = ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];

const MeetingDispositionMultiselect: React.FC<MeetingDispositionMultiselectProps> = ({
  selectedValues,
  onChange,
  isFetching = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Suppress unused variable warning - isFetching reserved for future loading states
  void isFetching;

  const handleToggleValue = (value: string) => {
    const option = ALL_DISPOSITION_OPTIONS.find(opt => opt.value === value);

    if (option?.isParent && option.children) {
      // Parent option clicked - toggle all children
      const parentDirectlySelected = selectedValues.includes(option.value);
      const allChildrenSelected = option.children.every(child => selectedValues.includes(child));

      if (parentDirectlySelected || allChildrenSelected) {
        // Currently selected (either parent directly or all children) - deselect
        // Remove parent value and all children
        onChange(selectedValues.filter(v => v !== option.value && !option.children!.includes(v)));
      } else {
        // Not selected - select all children (use children, not parent value for new selections)
        const newValues = selectedValues.filter(v => v !== option.value); // Remove parent if present
        option.children.forEach(child => {
          if (!newValues.includes(child)) {
            newValues.push(child);
          }
        });
        onChange(newValues);
      }
    } else if (option?.parent) {
      // Child option clicked - toggle the child and remove parent value if present
      const parentOption = ALL_DISPOSITION_OPTIONS.find(opt => opt.value === option.parent);
      let newValues = selectedValues.filter(v => v !== option.parent); // Remove parent value if present

      if (newValues.includes(value)) {
        // Deselect this child
        onChange(newValues.filter(v => v !== value));
      } else {
        // Select this child - if parent was directly selected, need to add the other children too
        if (selectedValues.includes(option.parent!) && parentOption?.children) {
          // Parent was directly selected, add all children except the clicked one (toggle effect)
          parentOption.children.forEach(child => {
            if (child !== value && !newValues.includes(child)) {
              newValues.push(child);
            }
          });
          onChange(newValues); // This effectively deselects the clicked child when parent was selected
        } else {
          onChange([...newValues, value]);
        }
      }
    } else {
      // Regular option (no parent/child relationship)
      if (selectedValues.includes(value)) {
        onChange(selectedValues.filter(v => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    }
  };

  // Check if a parent option should show as selected (parent directly selected OR all children selected)
  const isParentSelected = (option: typeof ALL_DISPOSITION_OPTIONS[0]) => {
    if (!option.isParent || !option.children) return false;
    // Parent is selected if directly selected OR all children are selected
    return selectedValues.includes(option.value) || option.children.every(child => selectedValues.includes(child));
  };

  // Check if a parent option is partially selected (some but not all children selected, and parent not directly selected)
  const isParentPartiallySelected = (option: typeof ALL_DISPOSITION_OPTIONS[0]) => {
    if (!option.isParent || !option.children) return false;
    // If parent is directly selected, it's fully selected not partial
    if (selectedValues.includes(option.value)) return false;
    const selectedChildrenCount = option.children.filter(child => selectedValues.includes(child)).length;
    return selectedChildrenCount > 0 && selectedChildrenCount < option.children.length;
  };

  // Get display badges - show parent label if all children selected OR parent is directly selected
  const getDisplayBadges = (): { value: string; label: string }[] => {
    const badges: { value: string; label: string }[] = [];
    const processedValues = new Set<string>();

    for (const option of ALL_DISPOSITION_OPTIONS) {
      if (option.isParent && option.children) {
        // Check if parent value is directly selected (legacy behavior)
        const parentDirectlySelected = selectedValues.includes(option.value);
        // Check if all children are selected
        const allChildrenSelected = option.children.every(child => selectedValues.includes(child));

        if (parentDirectlySelected || allChildrenSelected) {
          badges.push({ value: option.value, label: option.label });
          // Mark parent and all children as processed
          processedValues.add(option.value);
          option.children.forEach(child => processedValues.add(child));
        }
      }
    }

    // Add individual selected items that weren't covered by parent badges
    for (const value of selectedValues) {
      if (!processedValues.has(value)) {
        const option = ALL_DISPOSITION_OPTIONS.find(opt => opt.value === value);
        if (option) {
          badges.push({ value: option.value, label: option.label });
        }
      }
    }

    return badges;
  };

  // Clear all = empty array = "All Meeting Dispositions" (same as AccountValue)
  const handleClearAll = () => {
    onChange([]);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowTopShadow(target.scrollTop > 0);
    setShowBottomShadow(target.scrollTop < target.scrollHeight - target.clientHeight);
  };

  // Check if current selection matches the default "quality leads" filter
  // Need to normalize selections to base dispositions to compare properly
  // e.g., ENTERPRISE_DEAL + ENTERPRISE_ZERO should be treated as ENTERPRISE
  const normalizeToBaseDispositions = (values: string[]): Set<string> => {
    const normalized = new Set<string>();
    for (const v of values) {
      if (v === 'ENTERPRISE_DEAL' || v === 'ENTERPRISE_ZERO') {
        normalized.add('ENTERPRISE');
      } else if (v === 'TRANSACTIONAL_DEAL' || v === 'TRANSACTIONAL_ZERO') {
        normalized.add('TRANSACTIONAL');
      } else {
        normalized.add(v);
      }
    }
    return normalized;
  };

  const normalizedSelection = normalizeToBaseDispositions(selectedValues);

  const isDefaultQualityFilter =
    normalizedSelection.size === DEFAULT_QUALITY_FILTER.length &&
    DEFAULT_QUALITY_FILTER.every(val => normalizedSelection.has(val));

  // Filter is active when any values are selected
  const isFiltered = selectedValues.length > 0;

  return (
    <div className={`relative ${className}`}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer text-sm min-h-[2.5rem] flex items-center justify-between shadow-sm font-inter transition-all duration-200 ${
          isFiltered
            ? 'bg-gray-50 dark:bg-gray-600 border border-purple-200 dark:border-purple-700'
            : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
        }`}
      >
        <div className="flex items-center flex-1 mr-2">
          <div className="flex-1">
            {(() => {
              const badges = getDisplayBadges();
              if (selectedValues.length === 0 || badges.length === 0) {
                return <span className="text-gray-900 dark:text-gray-100">All Meeting Dispositions</span>;
              } else if (isDefaultQualityFilter) {
                return (
                  <span className="bg-purple-600 text-white px-2 py-1 rounded-lg text-xs font-medium font-inter whitespace-nowrap">
                    Quality Leads
                  </span>
                );
              } else {
                return (
                  <div className="flex flex-wrap gap-1">
                    {badges.map(badge => (
                      <span key={badge.value} className="bg-purple-600 text-white px-2 py-1 rounded-lg text-xs font-medium font-inter">
                        {badge.label}
                      </span>
                    ))}
                  </div>
                );
              }
            })()}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${
          isFiltered ? 'text-purple-500' : 'text-gray-500 dark:text-gray-400'
        }`} />
      </div>

      {isOpen && (
        <div className="absolute top-full mt-1 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-[9999] min-w-[280px]">
          {/* Top scroll shadow indicator */}
          {showTopShadow && (
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-gray-100 dark:from-gray-700 to-transparent pointer-events-none z-10 rounded-t-xl" />
          )}

          {/* Bottom scroll shadow indicator */}
          {showBottomShadow && (
            <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-gray-100 dark:from-gray-700 to-transparent pointer-events-none z-10 rounded-b-xl" />
          )}

          <div
            ref={dropdownRef}
            onScroll={handleScroll}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div
                onClick={handleClearAll}
                className={`px-3 py-2 text-sm font-medium font-inter text-right transition-all ${
                  selectedValues.length > 0
                    ? 'text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/30 cursor-pointer'
                    : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Clear All
              </div>
            </div>

            {/* Individual options */}
            <div className="py-1">
              {ALL_DISPOSITION_OPTIONS.map(option => {
                const isChildSelected = selectedValues.includes(option.value);
                const isParentFullySelected = option.isParent ? isParentSelected(option) : false;
                const isParentPartial = option.isParent ? isParentPartiallySelected(option) : false;
                const showSelected = option.isParent ? isParentFullySelected : isChildSelected;
                const showPartial = option.isParent && isParentPartial;

                return (
                  <div
                    key={option.value}
                    onClick={() => handleToggleValue(option.value)}
                    className={`py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm flex items-center justify-between font-inter ${
                      showSelected ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : showPartial ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400' : 'text-gray-700 dark:text-gray-300'
                    } ${option.indent ? 'pl-6 pr-3' : 'px-3'}`}
                  >
                    <span>{option.label}</span>
                    {showSelected && <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />}
                    {showPartial && <div className="h-4 w-4 flex items-center justify-center"><div className="h-2 w-2 bg-purple-400 rounded-sm" /></div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default MeetingDispositionMultiselect;